import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';
import {
  BreadcrumbDropdownMenuItem,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbTruncate,
} from '../breadcrumb.interfaces';
import { SidebarListItem } from '../../sidebar-list/sidebar-list.interfaces';
import { UrlFlagsService } from 'shared';

/**
 * Default truncation constraints per breadcrumb position (0-indexed).
 * Applied when the route config does not provide an explicit `truncate`.
 */
const DEFAULT_TRUNCATE_BY_INDEX: Record<number, BreadcrumbTruncate> = {
  0: { minWidth: '40px' },
  1: { minWidth: '0px' },
};

@Injectable()
export class BreadcrumbStoreService {
  private readonly itemsSignal = signal<BreadcrumbItem[]>([]);
  private readonly hiddenSignal = signal(false);
  private readonly minifiedSignal = signal(false);
  private readonly urlFlagsService = inject(UrlFlagsService);
  readonly items = this.itemsSignal.asReadonly();
  readonly isHidden = this.hiddenSignal.asReadonly();
  readonly minified = this.minifiedSignal.asReadonly();

  readonly selectedDropdownItem = signal<SidebarListItem | null>(null);

  /**
   * Dropdown menu items for the last breadcrumb (cached to avoid infinite loop).
   */
  readonly dropdownMenuItems = linkedSignal((): BreadcrumbDropdownMenuItem[] => {
    const items = this.items();
    if (items.length === 0) {
      return [];
    }
    const dropdownItems = items.at(-1)?.dropdown;
    if (!dropdownItems || dropdownItems.length === 0) {
      return [];
    }
    return dropdownItems.map(item => ({
      label: item.label,
      routerLink: item.route,
    }));
  });

  /**
   * All breadcrumb items mapped for the breadcrumb molecule renderer.
   */
  readonly breadcrumbItems = computed((): BreadcrumbMenuItem[] => {
    const items = this.items();
    const lastIndex = items.length - 1;
    const isMinified = this.minified();
    const isEmbedded = this.urlFlagsService.embedded();

    // In embedded mode, show only the last item (project link)
    if (isEmbedded && items.length > 0) {
      const lastItem = items[lastIndex];
      return [this.mapToBreadcrumbMenuItem(lastItem, true, 0)];
    }

    // In minified mode, show only the first and last item
    if (isMinified && items.length > 1) {
      const firstItem = items[0];
      const lastItem = items[lastIndex];
      return [
        this.mapToBreadcrumbMenuItem(firstItem, false, 0),
        this.mapToBreadcrumbMenuItem(lastItem, true, lastIndex),
      ];
    }

    return items.map((item, index) => {
      const isLast = index === lastIndex;
      return this.mapToBreadcrumbMenuItem(item, isLast, index);
    });
  });

  setIsHidden(isHidden: boolean): void {
    this.hiddenSignal.set(isHidden);
  }

  setItems(items: BreadcrumbItem[]): void {
    this.itemsSignal.set(items);
  }

  setMinified(minified: boolean): void {
    this.minifiedSignal.set(minified);
  }

  /**
   * Map BreadcrumbItem to BreadcrumbMenuItem.
   * Falls back to position-based default truncation when no explicit `truncate` is set.
   */
  mapToBreadcrumbMenuItem(
    item: BreadcrumbItem,
    isLast: boolean,
    index: number
  ): BreadcrumbMenuItem {
    return {
      label: item.label,
      routerLink: item.routerLink,
      data: {
        isLastItem: isLast,
        dropdown: item.dropdown,
        originalItem: item,
        icon: item.icon,
        footerDropdownActions: item.footerDropdownActions,
        clickable: item.clickable,
        truncate: item.truncate ?? DEFAULT_TRUNCATE_BY_INDEX[index],
      },
    };
  }
}
